import type { AttachmentDto, ConversationDto, MessageDto, PresenceDto, ServerEvent } from '@dijitalapp/shared';
import { api, ApiError, apiUpload, type ClientErrorCode } from './api';
import { outbox, uuidv7, type QueuedMessage } from './outbox';
import { RealtimeClient, type ConnectionState } from './realtime';

export type PendingStatus = 'queued' | 'sending' | 'failed';

export interface PendingMessage extends QueuedMessage {
  status: PendingStatus;
  error?: ClientErrorCode;
}

export interface ChatState {
  conversations: ConversationDto[];
  loaded: boolean;
  messages: Record<string, MessageDto[]>;
  hasMore: Record<string, boolean>;
  pending: Record<string, PendingMessage[]>;
  typing: Record<string, boolean>;
  /** By user id; only users whose presence the server lets us see. */
  presence: Record<string, PresenceDto>;
  connection: ConnectionState;
}

const TYPING_TTL_MS = 6_000;

function mergeMessages(existing: MessageDto[] = [], incoming: MessageDto[]): MessageDto[] {
  const byId = new Map(existing.map((m) => [m.id, m]));
  for (const m of incoming) byId.set(m.id, m);
  return [...byId.values()].sort((a, b) => a.seq - b.seq);
}

function sortConversations(list: ConversationDto[]): ConversationDto[] {
  const key = (c: ConversationDto) => c.lastMessage?.createdAt ?? '';
  return [...list].sort((a, b) => key(b).localeCompare(key(a)));
}

/**
 * Client chat state. Server events and REST results are merged idempotently (by message id and
 * clientMsgId), so duplicates and out-of-order delivery are harmless; seq gaps trigger a fetch.
 */
export class ChatStore {
  private state: ChatState = {
    conversations: [],
    loaded: false,
    messages: {},
    hasMore: {},
    pending: {},
    typing: {},
    presence: {},
    connection: 'connecting',
  };
  private listeners = new Set<() => void>();
  private typingTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private flushing = false;
  private readonly rt: RealtimeClient;
  /** Conversation currently on screen; incoming messages there are marked read. */
  private activeConversationId: string | null = null;

  constructor(
    private readonly myId: string,
    onSessionRevoked: () => void,
  ) {
    this.rt = new RealtimeClient(onSessionRevoked);
    this.rt.onEvent((e) => this.handle(e));
    this.rt.onState((connection) => {
      this.set({ connection });
      if (connection === 'connected') void this.sync();
    });
  }

  subscribe = (l: () => void) => {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  };

  getSnapshot = () => this.state;

  private set(patch: Partial<ChatState>) {
    this.state = { ...this.state, ...patch };
    this.listeners.forEach((l) => l());
  }

  async start(): Promise<void> {
    const queued = await outbox.all();
    const pending: Record<string, PendingMessage[]> = {};
    for (const q of queued) (pending[q.conversationId] ??= []).push({ ...q, status: 'queued' });
    this.set({ pending });
    await this.loadConversations().catch(() => undefined);
    void this.rt.start();
  }

  stop(): void {
    this.rt.stop();
    this.typingTimers.forEach(clearTimeout);
  }

  /** Called by the conversation view on mount (id) and unmount (null for that id). */
  setActive(id: string | null, ifCurrent?: string): void {
    if (ifCurrent === undefined || this.activeConversationId === ifCurrent) this.activeConversationId = id;
  }

  async loadConversations(): Promise<void> {
    const { conversations } = await api<{ conversations: ConversationDto[] }>('GET', '/conversations');
    this.set({ conversations: sortConversations(conversations), loaded: true });
  }

  private upsertConversation(c: ConversationDto) {
    const others = this.state.conversations.filter((x) => x.id !== c.id);
    this.set({ conversations: sortConversations([...others, c]) });
  }

  async refreshConversation(id: string): Promise<void> {
    try {
      this.upsertConversation(await api<ConversationDto>('GET', `/conversations/${id}`));
    } catch (err) {
      // Left the group or was removed: drop it from the list.
      if (err instanceof ApiError && err.code === 'NOT_FOUND') {
        this.set({ conversations: this.state.conversations.filter((c) => c.id !== id) });
        return;
      }
      throw err;
    }
  }

  async startDirect(userId: string): Promise<ConversationDto> {
    const c = await api<ConversationDto>('POST', '/conversations/direct', { userId });
    this.upsertConversation(c);
    return c;
  }

  /** Loads the newest page the first time, afterwards only what is missing (cursor sync). */
  async openConversation(id: string): Promise<void> {
    const known = this.state.messages[id];
    const last = known?.at(-1)?.seq;
    const query = last === undefined ? 'limit=50' : `afterSeq=${last}&limit=200`;
    const page = await api<{ messages: MessageDto[]; hasMore: boolean }>('GET', `/conversations/${id}/messages?${query}`);
    this.set({
      messages: { ...this.state.messages, [id]: mergeMessages(known, page.messages) },
      ...(last === undefined ? { hasMore: { ...this.state.hasMore, [id]: page.hasMore } } : {}),
    });
    if (last !== undefined && page.hasMore) await this.openConversation(id);
  }

  async loadOlder(id: string): Promise<void> {
    const first = this.state.messages[id]?.[0]?.seq;
    if (!first || first <= 1) return;
    const page = await api<{ messages: MessageDto[]; hasMore: boolean }>(
      'GET',
      `/conversations/${id}/messages?beforeSeq=${first}&limit=50`,
    );
    this.set({
      messages: { ...this.state.messages, [id]: mergeMessages(this.state.messages[id], page.messages) },
      hasMore: { ...this.state.hasMore, [id]: page.hasMore },
    });
  }

  /** After (re)connecting: refresh summaries and fetch anything the open conversation missed. */
  private async sync(): Promise<void> {
    try {
      await this.loadConversations();
      for (const id of Object.keys(this.state.messages)) await this.openConversation(id);
    } catch {
      /* next reconnect retries */
    }
    void this.flush();
  }

  // ---------- sending ----------

  async send(conversationId: string, body: string): Promise<void> {
    const item: PendingMessage = { clientMsgId: uuidv7(), conversationId, body, createdAt: new Date().toISOString(), status: 'queued' };
    this.setPending(conversationId, [...(this.state.pending[conversationId] ?? []), item]);
    await outbox.put(item);
    void this.flush();
  }

  /**
   * Uploads first (needs a connection), then queues the message that references the upload,
   * so a dropped connection after the upload still delivers it exactly once.
   */
  async sendAttachment(
    conversationId: string,
    file: Blob,
    name: string,
    opts: { caption?: string; voice?: boolean; durationMs?: number } = {},
  ): Promise<void> {
    const form = new FormData();
    if (opts.voice) {
      form.append('voice', 'true');
      form.append('durationMs', String(Math.round(opts.durationMs ?? 0)));
    }
    form.append('file', file, name);
    const { attachment } = await apiUpload<{ attachment: AttachmentDto }>(`/conversations/${conversationId}/attachments`, form);
    const item: PendingMessage = {
      clientMsgId: uuidv7(),
      conversationId,
      body: opts.caption ?? '',
      attachment,
      createdAt: new Date().toISOString(),
      status: 'queued',
    };
    this.setPending(conversationId, [...(this.state.pending[conversationId] ?? []), item]);
    await outbox.put(item);
    void this.flush();
  }

  private setPending(conversationId: string, list: PendingMessage[]) {
    this.set({ pending: { ...this.state.pending, [conversationId]: list } });
  }

  private updatePending(m: QueuedMessage, patch: Partial<PendingMessage> | null) {
    const list = this.state.pending[m.conversationId] ?? [];
    this.setPending(
      m.conversationId,
      patch === null ? list.filter((p) => p.clientMsgId !== m.clientMsgId) : list.map((p) => (p.clientMsgId === m.clientMsgId ? { ...p, ...patch } : p)),
    );
  }

  /** Sends queued messages in order. Network failures keep them queued; refusals mark them failed. */
  async flush(): Promise<void> {
    if (this.flushing || !navigator.onLine) return;
    this.flushing = true;
    try {
      const queue = Object.values(this.state.pending).flat().filter((p) => p.status === 'queued');
      queue.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
      for (const item of queue) {
        this.updatePending(item, { status: 'sending' });
        try {
          const message = await api<MessageDto>('POST', `/conversations/${item.conversationId}/messages`, {
            clientMsgId: item.clientMsgId,
            body: item.body,
            ...(item.attachment ? { attachmentId: item.attachment.id } : {}),
            clientSentAt: item.createdAt,
          });
          await outbox.remove(item.clientMsgId);
          this.updatePending(item, null);
          this.applyMessage(message);
        } catch (err) {
          const code = err instanceof ApiError ? err.code : 'UNKNOWN';
          if (code === 'NETWORK' || code === 'RATE_LIMITED' || code === 'UNKNOWN') {
            this.updatePending(item, { status: 'queued' });
            break; // keep order; retried on reconnect / next send
          }
          await outbox.remove(item.clientMsgId);
          this.updatePending(item, { status: 'failed', error: code });
        }
      }
    } finally {
      this.flushing = false;
    }
  }

  async retry(m: PendingMessage): Promise<void> {
    const item: PendingMessage = { ...m, status: 'queued' };
    delete item.error;
    this.updatePending(m, { status: 'queued', error: undefined });
    await outbox.put({
      clientMsgId: item.clientMsgId,
      conversationId: item.conversationId,
      body: item.body,
      createdAt: item.createdAt,
      ...(item.attachment ? { attachment: item.attachment } : {}),
    });
    void this.flush();
  }

  discard(m: PendingMessage): void {
    void outbox.remove(m.clientMsgId);
    this.updatePending(m, null);
  }

  async loadPresence(userIds: string[]): Promise<void> {
    if (userIds.length === 0) return;
    const { presence } = await api<{ presence: PresenceDto[] }>('GET', `/presence?userIds=${userIds.join(',')}`);
    const next = { ...this.state.presence };
    for (const id of userIds) delete next[id];
    for (const p of presence) next[p.userId] = p;
    this.set({ presence: next });
  }

  // ---------- edit & delete ----------

  async editMessage(conversationId: string, messageId: string, body: string): Promise<void> {
    this.applyUpdate(await api<MessageDto>('PATCH', `/conversations/${conversationId}/messages/${messageId}`, { body }));
  }

  async deleteMessage(conversationId: string, messageId: string, scope: 'everyone' | 'me'): Promise<void> {
    const result = await api<MessageDto | undefined>('DELETE', `/conversations/${conversationId}/messages/${messageId}?scope=${scope}`);
    if (result) this.applyUpdate(result);
    else {
      // Hidden for me only: drop it locally and refresh the list preview.
      const list = (this.state.messages[conversationId] ?? []).filter((m) => m.id !== messageId);
      this.set({ messages: { ...this.state.messages, [conversationId]: list } });
      void this.refreshConversation(conversationId).catch(() => undefined);
    }
  }

  /** Edits and deletions replace the message in place (same id and seq). */
  private applyUpdate(m: MessageDto) {
    const known = this.state.messages[m.conversationId];
    if (known?.some((x) => x.id === m.id)) {
      this.set({ messages: { ...this.state.messages, [m.conversationId]: mergeMessages(known, [m]) } });
    }
    const c = this.state.conversations.find((x) => x.id === m.conversationId);
    if (c?.lastMessage?.id === m.id) this.upsertConversation({ ...c, lastMessage: m });
  }

  // ---------- receipts & typing ----------

  markRead(conversationId: string): void {
    const c = this.state.conversations.find((x) => x.id === conversationId);
    if (!c || document.visibilityState !== 'visible' || c.lastSeq <= c.myLastReadSeq) return;
    this.upsertConversation({ ...c, myLastReadSeq: c.lastSeq, unreadCount: 0 });
    void api('POST', `/conversations/${conversationId}/read`, { seq: c.lastSeq }).catch(() => undefined);
  }

  setTyping(conversationId: string, typing: boolean): void {
    this.rt.command('typing', { conversationId, typing });
  }

  // ---------- server events ----------

  private applyMessage(m: MessageDto) {
    const known = this.state.messages[m.conversationId];
    const lastKnown = known?.at(-1)?.seq ?? 0;
    if (known) {
      this.set({ messages: { ...this.state.messages, [m.conversationId]: mergeMessages(known, [m]) } });
      // A gap means we missed events (e.g. a dropped socket): fetch the range.
      if (m.seq > lastKnown + 1) void this.openConversation(m.conversationId).catch(() => undefined);
    }
    const pending = this.state.pending[m.conversationId];
    if (pending?.some((p) => p.clientMsgId === m.clientMsgId)) {
      this.setPending(m.conversationId, pending.filter((p) => p.clientMsgId !== m.clientMsgId));
    }

    const c = this.state.conversations.find((x) => x.id === m.conversationId);
    if (!c) {
      void this.loadConversations().catch(() => undefined);
      return;
    }
    const mine = m.senderId === this.myId;
    if (m.seq <= c.lastSeq && c.lastMessage && c.lastMessage.seq >= m.seq) return;
    this.upsertConversation({
      ...c,
      lastSeq: Math.max(c.lastSeq, m.seq),
      lastMessage: m,
      myLastReadSeq: mine ? Math.max(c.myLastReadSeq, m.seq) : c.myLastReadSeq,
      unreadCount: mine ? c.unreadCount : c.unreadCount + 1,
    });
    if (!mine && this.activeConversationId === m.conversationId) this.markRead(m.conversationId);
  }

  private handle(e: ServerEvent) {
    switch (e.event) {
      case 'message.created':
        this.applyMessage(e.payload);
        if (e.payload.senderId !== this.myId) this.setTypingState(e.payload.conversationId, false);
        return;
      case 'presence.updated':
        this.set({ presence: { ...this.state.presence, [e.payload.userId]: e.payload } });
        return;
      case 'message.updated':
        this.applyUpdate(e.payload);
        return;
      case 'receipt.updated': {
        const c = this.state.conversations.find((x) => x.id === e.conversation_id);
        if (!c || e.payload.userId === this.myId) return;
        // Groups show "read by everyone" = minimum over members, which only the server knows.
        if (c.type === 'group') {
          void this.refreshConversation(c.id).catch(() => undefined);
          return;
        }
        this.upsertConversation({
          ...c,
          peerLastDeliveredSeq: Math.max(c.peerLastDeliveredSeq, e.payload.deliveredSeq),
          peerLastReadSeq: Math.max(c.peerLastReadSeq, e.payload.readSeq),
        });
        return;
      }
      case 'typing.updated':
        if (e.conversation_id && e.payload.userId !== this.myId) this.setTypingState(e.conversation_id, e.payload.typing);
        return;
      case 'conversation.updated':
        void this.refreshConversation(e.payload.conversationId).catch(() => undefined);
        return;
      default:
        return;
    }
  }

  private setTypingState(conversationId: string, typing: boolean) {
    const timer = this.typingTimers.get(conversationId);
    if (timer) clearTimeout(timer);
    if (typing) {
      // Stale typing indicators expire even if the "stopped" event is lost.
      this.typingTimers.set(conversationId, setTimeout(() => this.setTypingState(conversationId, false), TYPING_TTL_MS));
    }
    if (Boolean(this.state.typing[conversationId]) !== typing) {
      this.set({ typing: { ...this.state.typing, [conversationId]: typing } });
    }
  }
}
